import {
  collectClauseTextHints,
  MIN_CLAUSE_HINT_LEN,
} from "../utils/clauseLocateHints";
import {
  foldTypographicPunctuation,
  planParagraphMutation,
  type MutationOp,
} from "../utils/paragraphMutationPlan";
import {
  mergeAdjacentStyleTokens,
  styleHintsToReapply,
  type ParagraphStyleHint,
} from "../utils/paragraphStyleHints";
import { clearYellowHighlightsInParagraph } from "./highlightScanner";

export const navigateToSection = async (sectionRef: string): Promise<void> => {
  try {
    if (typeof Word !== "undefined") {
      await Word.run(async (context) => {
        const searchResults = context.document.body.search(sectionRef, { matchCase: false });
        context.load(searchResults, "items");
        await context.sync();
        if (searchResults.items.length > 0) {
          searchResults.items[0].select();
          await context.sync();
        }
      });
    }
  } catch {
    console.warn(`Could not navigate to section: ${sectionRef}`);
  }
};

export type NavigateIndexesResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly message: string };

const userFacingNavigateError = (): string =>
  "We could not move to that place in the document. The contract in Word may not match this analysis.";

const userFacingWordBusy = (): string => "Another Word operation is in progress. Please wait.";
const userFacingHintRequired = (): string =>
  "This suggestion does not include enough text to locate a clause in the document.";

export type NativeDocumentSaveResult =
  | { readonly ok: true }
  | {
      readonly ok: false;
      readonly reason: "unavailable" | "read_only" | "failed";
      readonly message: string;
    };

export type DocumentChangeTrackingMode =
  | Word.ChangeTrackingMode
  | "Off"
  | "TrackAll"
  | "TrackMineOnly";

export type DocumentChangeTrackingModeResult =
  | { readonly ok: true; readonly mode: DocumentChangeTrackingMode }
  | {
      readonly ok: false;
      readonly reason: "unavailable" | "failed";
      readonly message: string;
    };

export type SetDocumentChangeTrackingOffStrictResult =
  | { readonly ok: true; readonly mode: DocumentChangeTrackingMode }
  | {
      readonly ok: false;
      readonly reason: "unavailable" | "failed";
      readonly message: string;
      readonly mode?: DocumentChangeTrackingMode;
    };

const isLikelyReadOnlySaveMessage = (message: string): boolean => {
  const haystack = message.toLowerCase();
  return (
    /readonlyexception/.test(haystack) ||
    /read[\s-]?only/.test(haystack) ||
    /viewing/.test(haystack) ||
    /cannot edit/.test(haystack) ||
    /not allowed.*edit/.test(haystack) ||
    /protected/.test(haystack)
  );
};

const normalizeTrackingMode = (
  mode: DocumentChangeTrackingMode | null | undefined,
): string => (mode == null ? "" : String(mode).trim().toLowerCase());

/**
 * Treat both canonical and enum-string forms as enabled.
 */
export const isTrackChangesModeEnabled = (
  mode: DocumentChangeTrackingMode | null | undefined,
): boolean => {
  const normalized = normalizeTrackingMode(mode);
  return normalized === "trackall" || normalized === "trackmineonly";
};

/**
 * Save the current host document using Word's native save semantics.
 * This avoids full .docx package overwrite that can create broad revision noise.
 */
export const saveHostDocumentNative = async (): Promise<NativeDocumentSaveResult> => {
  if (typeof Office === "undefined") {
    return {
      ok: false,
      reason: "unavailable",
      message: "Office.js is not loaded in this host.",
    };
  }

  try {
    await Office.onReady();
  } catch {
    return {
      ok: false,
      reason: "unavailable",
      message: "Office host is not ready for save.",
    };
  }

  const docWithSave = Office.context?.document as
    | (Office.Document & {
        saveAsync?: (
          callback: (result: Office.AsyncResult<void>) => void
        ) => void;
      })
    | undefined;
  const saveAsync = docWithSave?.saveAsync;
  if (typeof saveAsync !== "function") {
    return {
      ok: false,
      reason: "unavailable",
      message: "Native save is not available in this Word host.",
    };
  }

  return new Promise<NativeDocumentSaveResult>((resolve) => {
    saveAsync((result: Office.AsyncResult<void>) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve({ ok: true });
        return;
      }
      const detail = result.error?.message?.trim() || "Word could not save the document.";
      if (isLikelyReadOnlySaveMessage(detail)) {
        resolve({
          ok: false,
          reason: "read_only",
          message:
            "Word is in read-only/view mode. Switch to Edit mode and try saving again.",
        });
        return;
      }
      resolve({
        ok: false,
        reason: "failed",
        message: detail.slice(0, 300),
      });
    });
  });
};

let officeMutationInFlight = false;

/**
 * Paragraphs we applied navigation yellow to (FR-014). On each navigate we clear yellow in
 * each tracked paragraph; locations that fail to resolve stay queued for the next attempt.
 */
let navigationHighlightHistory: Array<{
  readonly clauseTextHints: readonly string[];
}> = [];

const clearTrackedNavigationHighlights = async (
  context: Word.RequestContext,
): Promise<void> => {
  for (const loc of navigationHighlightHistory) {
    try {
      const prev = await findBodyParagraphByHints(context, loc.clauseTextHints);
      if (prev) {
        await clearYellowHighlightsInParagraph(context, prev);
      }
    } catch {
      /* paragraph may no longer resolve */
    }
  }
  navigationHighlightHistory = [];
};

/** Optional hint(s) so paragraph resolution matches the clause body when indexes drift. */
export type SuggestionLocationOptions = {
  readonly clauseTextHint?: string | null;
  /** Tried in order with {@link clauseTextHint}; use after approve when live text may be AI/approved. */
  readonly clauseTextHints?: readonly (string | null | undefined)[];
};

/**
 * Prefer manifest-like text (`client_suggested_text` when present, else `original_text`);
 * fall back to the other field for disambiguation when the primary string is short.
 */
export const clauseTextHintFromSuggestion = (
  originalText: string | null | undefined,
  clientSuggestedText: string | null | undefined
): string | null => {
  const client = clientSuggestedText?.trim() ?? "";
  if (client !== "") {
    return client;
  }
  const original = originalText?.trim() ?? "";
  return original !== "" ? original : null;
};

const hintsFromLocationOptions = (
  options?: SuggestionLocationOptions | null,
): string[] =>
  collectClauseTextHints(
    ...(options?.clauseTextHints ?? []),
    options?.clauseTextHint,
  );

const normalizeWs = (s: string): string => s.replace(/\s+/g, " ").trim();

/**
 * Scores a paragraph against the clause hint so we can pick the best whole-document match.
 */
function scoreParagraphAgainstNeedle(
  pNormalized: string,
  needle: string,
  shortNeedle: string
): number {
  if (needle.length >= 12 && pNormalized.includes(needle)) {
    return 100;
  }
  if (needle.length >= 40 && pNormalized.includes(needle.slice(0, 120))) {
    return 85;
  }
  if (pNormalized.includes(shortNeedle)) {
    return 70;
  }
  const hw = needle
    .split(/\s+/)
    .map((w) => w.replace(/[^\w]/g, ""))
    .filter((w) => w.length > 2)
    .slice(0, 10);
  if (hw.length > 0) {
    const matches = hw.filter((w) => pNormalized.toLowerCase().includes(w.toLowerCase())).length;
    return Math.round((matches / hw.length) * 55);
  }
  return 0;
}

/** Minimum score to accept a whole-document text match (avoids selecting an unrelated paragraph). */
const BODY_HINT_MATCH_MIN_SCORE = 60;

/**
 * Locate the target paragraph by one or more clause texts across the document body.
 * Picks the paragraph with the best score against any candidate hint.
 */
async function findBodyParagraphByHints(
  context: Word.RequestContext,
  clauseTextHints: readonly string[],
): Promise<Word.Paragraph | null> {
  const needles = clauseTextHints
    .map((h) => normalizeWs(h).slice(0, 200))
    .filter((n) => n.length >= MIN_CLAUSE_HINT_LEN);
  if (needles.length === 0) {
    return null;
  }
  const paragraphs = context.document.body.paragraphs;
  paragraphs.load("items");
  await context.sync();
  const items = paragraphs.items;
  if (items.length === 0) {
    return null;
  }
  for (const it of items) {
    it.load("text");
  }
  await context.sync();
  let bestIdx = -1;
  let bestScore = -1;
  for (let i = 0; i < items.length; i++) {
    const p = normalizeWs(items[i]!.text ?? "");
    let score = 0;
    for (const needle of needles) {
      score = Math.max(
        score,
        scoreParagraphAgainstNeedle(p, needle, needle.slice(0, 60)),
      );
    }
    if (score > bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  }
  if (bestIdx < 0 || bestScore < BODY_HINT_MATCH_MIN_SCORE) {
    return null;
  }
  return items[bestIdx] ?? null;
}

const tryHighlightParagraph = (paragraph: Word.Paragraph): void => {
  try {
    const range = paragraph.getRange();
    range.font.highlightColor = "Yellow";
    try {
      const wide = range as Word.Range & { highlightColorIndex?: string };
      wide.highlightColorIndex = "Yellow" as unknown as Word.Range["highlightColorIndex"];
    } catch {
      /* some hosts use font.highlightColor only */
    }
  } catch {
    /* optional UX */
  }
};

async function resolveParagraphByHints(
  context: Word.RequestContext,
  clauseTextHints: readonly string[],
): Promise<Word.Paragraph> {
  if (clauseTextHints.length === 0) {
    throw new Error("HINT_REQUIRED");
  }
  const paragraph = await findBodyParagraphByHints(context, clauseTextHints);
  if (!paragraph) {
    throw new Error("PARAGRAPH_NOT_FOUND");
  }
  return paragraph;
}

async function resolveParagraphFromOptions(
  context: Word.RequestContext,
  options?: SuggestionLocationOptions | null,
): Promise<{ readonly paragraph: Word.Paragraph; readonly hints: readonly string[] }> {
  const hints = hintsFromLocationOptions(options);
  const paragraph = await resolveParagraphByHints(context, hints);
  return { paragraph, hints };
}

/**
 * Selects a paragraph using optional section scope and a paragraph index within that body.
 * Does not mutate suggestion objects from the API.
 */
export const navigateToSuggestionIndexes = async (
  sectionIndex: number | null,
  contentIndex: number | null,
  options?: SuggestionLocationOptions
): Promise<NavigateIndexesResult> => {
  void sectionIndex;
  void contentIndex;

  if (typeof Word === "undefined") {
    return { ok: false, message: "Word is not available in this environment." };
  }

  const locateHints = hintsFromLocationOptions(options);
  if (locateHints.length === 0) {
    return { ok: false, message: userFacingHintRequired() };
  }

  try {
    await Word.run(async (context) => {
      await clearTrackedNavigationHighlights(context);

      const { paragraph, hints } = await resolveParagraphFromOptions(
        context,
        options,
      );
      paragraph.select();
      tryHighlightParagraph(paragraph);
      await context.sync();
      navigationHighlightHistory = [{ clauseTextHints: hints }];
    });
    return { ok: true };
  } catch (e) {
    console.warn("navigateToSuggestionIndexes failed", e);
    if (e instanceof Error && e.message === "HINT_REQUIRED") {
      return { ok: false, message: userFacingHintRequired() };
    }
    return { ok: false, message: userFacingNavigateError() };
  }
};

/**
 * Clears all clause navigation highlights tracked by this add-in session.
 */
export const clearAllNavigationHighlights = async (): Promise<void> => {
  if (typeof Word === "undefined" || navigationHighlightHistory.length === 0) {
    return;
  }
  try {
    await Word.run(async (context) => {
      await clearTrackedNavigationHighlights(context);
      await context.sync();
    });
  } catch (e) {
    console.warn("clearAllNavigationHighlights failed", e);
  }
};

const isUnderlineOn = (value: string | boolean | null | undefined): boolean => {
  if (value == null || value === false) {
    return false;
  }
  if (value === true) {
    return true;
  }
  const s = String(value).trim().toLowerCase();
  return s !== "" && s !== "none" && s !== "null" && s !== "undefined";
};

/**
 * Capture bold/italic/underline tokens from the live paragraph before a full replace.
 */
async function captureParagraphStyleHints(
  context: Word.RequestContext,
  paragraph: Word.Paragraph,
): Promise<ParagraphStyleHint[]> {
  try {
    const range = paragraph.getRange();
    range.load("text");
    await context.sync();
    if (!(range.text ?? "").trim()) {
      return [];
    }
    const parts = range.split([" ", "\t"], true, true);
    context.load(parts, "items");
    await context.sync();
    if (parts.items.length === 0) {
      return [];
    }
    for (const item of parts.items) {
      item.load("text");
      item.font.load(["bold", "italic", "underline"]);
    }
    await context.sync();
    const tokens: ParagraphStyleHint[] = [];
    for (const item of parts.items) {
      const text = (item.text ?? "").replace(/\s+/g, " ").trim();
      if (text.length < 3) {
        continue;
      }
      tokens.push({
        text,
        bold: item.font.bold === true,
        italic: item.font.italic === true,
        underline: isUnderlineOn(item.font.underline),
      });
    }
    return mergeAdjacentStyleTokens(tokens);
  } catch (e) {
    console.warn("captureParagraphStyleHints failed", e);
    return [];
  }
}

async function reapplyParagraphStyleHints(
  context: Word.RequestContext,
  paragraph: Word.Paragraph,
  hints: readonly ParagraphStyleHint[],
  newText: string,
): Promise<void> {
  const toApply = styleHintsToReapply(hints, newText);
  if (toApply.length === 0) {
    return;
  }
  try {
    const scope = paragraph.getRange();
    for (const hint of toApply) {
      const results = scope.search(hint.text, {
        matchCase: true,
        matchWholeWord: false,
      });
      context.load(results, "items");
      await context.sync();
      if (results.items.length === 0) {
        continue;
      }
      const target = results.items[0]!;
      if (hint.bold) {
        target.font.bold = true;
      }
      if (hint.italic) {
        target.font.italic = true;
      }
      if (hint.underline) {
        // Hosts accept string underline styles; typings vary across Office.js versions.
        (target.font as Word.Font & { underline: string }).underline = "Single";
      }
      await context.sync();
    }
  } catch (e) {
    console.warn("reapplyParagraphStyleHints failed", e);
  }
}

/** Build alternate needles so Word.search still hits curly/straight quote variants. */
const quoteSearchVariants = (needle: string): string[] => {
  const variants = [needle];
  const straight = Array.from(needle, (ch) => foldTypographicPunctuation(ch)).join("");
  if (straight !== needle) {
    variants.push(straight);
  }
  const curly = straight
    .replace(/"/g, "\u201C")
    .replace(/'/g, "\u2019");
  if (curly !== needle && curly !== straight) {
    variants.push(curly);
  }
  // Also try closing-curly-only pattern used by Word for trailing quotes.
  const mixed = straight.replace(/"/g, "\u201D").replace(/'/g, "\u2019");
  if (!variants.includes(mixed)) {
    variants.push(mixed);
  }
  return variants;
};

async function searchFirstInParagraph(
  context: Word.RequestContext,
  paragraph: Word.Paragraph,
  needle: string,
): Promise<Word.Range | null> {
  const trimmed = needle.trim();
  if (!trimmed) {
    return null;
  }
  const scope = paragraph.getRange();
  for (const candidate of quoteSearchVariants(trimmed)) {
    const results = scope.search(candidate, {
      matchCase: false,
      matchWholeWord: false,
    });
    context.load(results, "items");
    await context.sync();
    if (results.items.length > 0) {
      return results.items[0] ?? null;
    }
  }
  return null;
}

/**
 * Apply local span mutations from the end of the paragraph forward.
 * Returns false if any op cannot be located (caller should full-replace to recover).
 */
async function tryApplyPartialOps(
  context: Word.RequestContext,
  paragraph: Word.Paragraph,
  ops: readonly MutationOp[],
): Promise<boolean> {
  const ordered = [...ops].sort((a, b) => b.start - a.start);
  for (const op of ordered) {
    if (op.kind === "insert") {
      const anchorText = op.anchorBefore?.trim() ?? "";
      if (!anchorText) {
        return false;
      }
      const anchor = await searchFirstInParagraph(context, paragraph, anchorText);
      if (!anchor) {
        return false;
      }
      anchor.insertText(op.newSnippet, Word.InsertLocation.after);
      await context.sync();
      continue;
    }
    const range = await searchFirstInParagraph(context, paragraph, op.oldSnippet);
    if (!range) {
      return false;
    }
    range.insertText(op.newSnippet, Word.InsertLocation.replace);
    await context.sync();
  }
  return true;
}

async function clearInheritedRunFormatting(
  context: Word.RequestContext,
  paragraph: Word.Paragraph,
): Promise<void> {
  try {
    const range = paragraph.getRange();
    range.font.bold = false;
    range.font.italic = false;
    (range.font as Word.Font & { underline: string }).underline = "None";
    await context.sync();
  } catch (e) {
    console.warn("clearInheritedRunFormatting failed", e);
  }
}

async function applyFullParagraphReplace(
  context: Word.RequestContext,
  paragraph: Word.Paragraph,
  newText: string,
): Promise<void> {
  const hints = await captureParagraphStyleHints(context, paragraph);
  const range = paragraph.getRange();
  range.insertText(newText, Word.InsertLocation.replace);
  await context.sync();
  // insertText(replace) inherits the first run's bold/italic — reset then reapply hints.
  await clearInheritedRunFormatting(context, paragraph);
  await reapplyParagraphStyleHints(context, paragraph, hints, newText);
}

/**
 * Reject tracked changes in a paragraph (undo approve markup → pre-approve text).
 */
async function rejectParagraphTrackedChanges(
  context: Word.RequestContext,
  paragraph: Word.Paragraph,
): Promise<{ readonly ok: boolean; readonly hadChanges: boolean }> {
  const tryReject = async (
    getCollection: () => Word.TrackedChangeCollection,
  ): Promise<{ readonly ok: boolean; readonly hadChanges: boolean } | null> => {
    try {
      const collection = getCollection();
      context.load(collection, "items");
      await context.sync();
      const hadChanges = (collection.items?.length ?? 0) > 0;
      if (hadChanges) {
        collection.rejectAll();
        await context.sync();
      }
      return { ok: true, hadChanges };
    } catch (e) {
      console.warn("rejectParagraphTrackedChanges attempt failed", e);
      return null;
    }
  };

  const viaParagraph = await tryReject(() => paragraph.getTrackedChanges());
  if (viaParagraph) {
    return viaParagraph;
  }
  const viaRange = await tryReject(() => paragraph.getRange().getTrackedChanges());
  if (viaRange) {
    return viaRange;
  }
  return { ok: false, hadChanges: false };
}

/**
 * Prefer Word's "current" reviewed text when track-change markup is present.
 */
async function readParagraphCurrentText(
  context: Word.RequestContext,
  paragraph: Word.Paragraph,
): Promise<string> {
  try {
    const reviewed = paragraph.getReviewedText(
      Word.ChangeTrackingVersion.current,
    );
    await context.sync();
    const value = typeof reviewed.value === "string" ? reviewed.value : "";
    if (value.trim() !== "") {
      return value;
    }
  } catch {
    /* fall through to paragraph.text */
  }
  paragraph.load("text");
  await context.sync();
  return paragraph.text ?? "";
}

type ApplyByPlanOptions = {
  /** Skip yellow highlight (restore). */
  readonly skipHighlight?: boolean;
  /** Skip partial path after accept failures / heavy revision cleanup. */
  readonly forceFull?: boolean;
};

/**
 * Shared approve/restore engine: plan mutation then partial or full apply.
 */
async function applyTextToParagraphByPlan(
  context: Word.RequestContext,
  paragraph: Word.Paragraph,
  newText: string,
  logLabel: "applyClause" | "restoreClause",
  applyOptions?: ApplyByPlanOptions,
): Promise<void> {
  const oldText = await readParagraphCurrentText(context, paragraph);
  const plan = applyOptions?.forceFull
    ? ({ mode: "full", reason: "force_full" } as const)
    : planParagraphMutation(oldText, newText);

  if (plan.mode === "noop") {
    console.info(`[${logLabel}] mode=noop`);
    paragraph.select();
    if (!applyOptions?.skipHighlight) {
      tryHighlightParagraph(paragraph);
    }
    await context.sync();
    return;
  }

  if (plan.mode === "partial") {
    const ok = await tryApplyPartialOps(context, paragraph, plan.ops);
    if (ok) {
      console.info(`[${logLabel}] mode=partial ops=${plan.ops.length}`);
      paragraph.select();
      if (!applyOptions?.skipHighlight) {
        tryHighlightParagraph(paragraph);
      }
      await context.sync();
      return;
    }
    console.info(
      `[${logLabel}] mode=partial failed search; falling back to full`,
    );
  } else {
    console.info(`[${logLabel}] mode=full reason=${plan.reason}`);
  }

  await applyFullParagraphReplace(context, paragraph, newText);
  paragraph.select();
  if (!applyOptions?.skipHighlight) {
    tryHighlightParagraph(paragraph);
  }
  await context.sync();
}

/**
 * Enables track changes and applies approved clause text.
 * Keeps Track Changes markup visible so the user can see what changed (no acceptAll).
 * Prefers partial span replaces; falls back to full paragraph replace with style re-apply.
 */
export const applyClauseAtSuggestionIndexes = async (
  sectionIndex: number | null,
  contentIndex: number | null,
  newText: string,
  options?: SuggestionLocationOptions
): Promise<NavigateIndexesResult> => {
  void sectionIndex;
  void contentIndex;

  if (typeof Word === "undefined") {
    return { ok: false, message: "Word is not available in this environment." };
  }
  if (officeMutationInFlight) {
    return { ok: false, message: userFacingWordBusy() };
  }
  if (hintsFromLocationOptions(options).length === 0) {
    return { ok: false, message: userFacingHintRequired() };
  }
  officeMutationInFlight = true;
  try {
    await Word.run(async (context) => {
      const doc = context.document;
      doc.load("changeTrackingMode");
      await context.sync();
      const savedMode = doc.changeTrackingMode;
      const alreadyTrackAll =
        String(savedMode) === String(Word.ChangeTrackingMode.trackAll);
      try {
        if (!alreadyTrackAll) {
          doc.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
          await context.sync();
        }
        const { paragraph } = await resolveParagraphFromOptions(context, options);
        await applyTextToParagraphByPlan(
          context,
          paragraph,
          newText,
          "applyClause",
        );
        console.info(
          "[applyClause] left Track Changes markup visible (not accepted)",
        );
      } finally {
        if (!alreadyTrackAll) {
          try {
            // Restore prior recording mode; markup from this approve remains visible.
            doc.changeTrackingMode = savedMode;
            await context.sync();
          } catch {
            /* best-effort restore */
          }
        }
      }
    });
    return { ok: true };
  } catch (e) {
    console.warn("applyClauseAtSuggestionIndexes failed", e);
    return { ok: false, message: userFacingNavigateError() };
  } finally {
    officeMutationInFlight = false;
  }
};

/**
 * Enables track changes and removes the target paragraph text.
 */
export const removeClauseAtSuggestionIndexes = async (
  sectionIndex: number | null,
  contentIndex: number | null,
  options?: SuggestionLocationOptions
): Promise<NavigateIndexesResult> => {
  void sectionIndex;
  void contentIndex;

  if (typeof Word === "undefined") {
    return { ok: false, message: "Word is not available in this environment." };
  }
  if (officeMutationInFlight) {
    return { ok: false, message: userFacingWordBusy() };
  }
  if (hintsFromLocationOptions(options).length === 0) {
    return { ok: false, message: userFacingHintRequired() };
  }
  officeMutationInFlight = true;
  try {
    await Word.run(async (context) => {
      const doc = context.document;
      doc.load("changeTrackingMode");
      await context.sync();
      const savedMode = doc.changeTrackingMode;
      const alreadyTrackAll =
        String(savedMode) === String(Word.ChangeTrackingMode.trackAll);
      try {
        if (!alreadyTrackAll) {
          doc.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
          await context.sync();
        }
        const { paragraph } = await resolveParagraphFromOptions(context, options);
        const range = paragraph.getRange();
        range.insertText("", Word.InsertLocation.replace);
        paragraph.select();
        tryHighlightParagraph(paragraph);
        await context.sync();
      } finally {
        if (!alreadyTrackAll) {
          try {
            doc.changeTrackingMode = savedMode;
            await context.sync();
          } catch {
            /* best-effort restore */
          }
        }
      }
    });
    return { ok: true };
  } catch (e) {
    console.warn("removeClauseAtSuggestionIndexes failed", e);
    return { ok: false, message: userFacingNavigateError() };
  } finally {
    officeMutationInFlight = false;
  }
};

/**
 * Clears yellow navigation highlight for a single suggestion paragraph.
 */
export const clearSuggestionHighlightAtIndexes = async (
  sectionIndex: number | null,
  contentIndex: number | null,
  options?: SuggestionLocationOptions
): Promise<NavigateIndexesResult> => {
  void sectionIndex;
  void contentIndex;

  if (typeof Word === "undefined") {
    return { ok: false, message: "Word is not available in this environment." };
  }
  if (officeMutationInFlight) {
    return { ok: false, message: userFacingWordBusy() };
  }
  const locateHints = hintsFromLocationOptions(options);
  if (locateHints.length === 0) {
    return { ok: false, message: userFacingHintRequired() };
  }

  try {
    await Word.run(async (context) => {
      const { paragraph } = await resolveParagraphFromOptions(context, options);
      await clearYellowHighlightsInParagraph(context, paragraph);
      await context.sync();
    });
    const hintKeys = new Set(
      locateHints.map((h) => normalizeWs(h).toLowerCase()),
    );
    navigationHighlightHistory = navigationHighlightHistory.filter((loc) => {
      const overlaps = loc.clauseTextHints.some((h) =>
        hintKeys.has(normalizeWs(h).toLowerCase()),
      );
      return !overlaps;
    });
    return { ok: true };
  } catch (e) {
    console.warn("clearSuggestionHighlightAtIndexes failed", e);
    if (e instanceof Error && e.message === "HINT_REQUIRED") {
      return { ok: false, message: userFacingHintRequired() };
    }
    return { ok: false, message: userFacingNavigateError() };
  }
};

/** Turns track changes off (e.g. after final approve). */
export const setDocumentChangeTrackingOff = async (): Promise<void> => {
  if (typeof Word === "undefined") {
    return;
  }
  try {
    await Word.run(async (context) => {
      context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();
    });
  } catch (e) {
    console.warn("setDocumentChangeTrackingOff failed", e);
  }
};

/**
 * Reads the current document track-changes mode using a safe structured result.
 */
export const getDocumentChangeTrackingMode =
  async (): Promise<DocumentChangeTrackingModeResult> => {
    if (typeof Word === "undefined") {
      return {
        ok: false,
        reason: "unavailable",
        message: "Word is not available in this environment.",
      };
    }
    try {
      const mode = await Word.run(async (context) => {
        const doc = context.document;
        doc.load("changeTrackingMode");
        await context.sync();
        return doc.changeTrackingMode as DocumentChangeTrackingMode;
      });
      return { ok: true, mode };
    } catch (e) {
      const detail =
        e instanceof Error && e.message
          ? e.message
          : "Unable to read Word track changes mode.";
      return {
        ok: false,
        reason: "failed",
        message: detail.slice(0, 300),
      };
    }
  };

/**
 * Forces Track Changes off and verifies the resulting mode.
 */
export const setDocumentChangeTrackingOffStrict =
  async (): Promise<SetDocumentChangeTrackingOffStrictResult> => {
    if (typeof Word === "undefined") {
      return {
        ok: false,
        reason: "unavailable",
        message: "Word is not available in this environment.",
      };
    }
    try {
      const mode = await Word.run(async (context) => {
        const doc = context.document;
        doc.changeTrackingMode = Word.ChangeTrackingMode.off;
        doc.load("changeTrackingMode");
        await context.sync();
        return doc.changeTrackingMode as DocumentChangeTrackingMode;
      });
      if (isTrackChangesModeEnabled(mode)) {
        return {
          ok: false,
          reason: "failed",
          message:
            "Track Changes is still on in Word. Turn it off in Review before opening this contract.",
          mode,
        };
      }
      return { ok: true, mode };
    } catch (e) {
      const detail =
        e instanceof Error && e.message
          ? e.message
          : "Unable to turn off Track Changes in Word.";
      return {
        ok: false,
        reason: "failed",
        message: `Could not turn off Track Changes in Word. ${detail}`.slice(0, 320),
      };
    }
  };

/**
 * Restores paragraph toward baseline text without recording revisions.
 * If approve markup is still present: rejectAll (undo TC) then assert baseline.
 * If already clean (e.g. reopen): plan/apply baseline text only.
 */
export const restoreParagraphToOriginal = async (
  sectionIndex: number | null,
  contentIndex: number | null,
  baselineText: string,
  options?: SuggestionLocationOptions
): Promise<NavigateIndexesResult> => {
  void sectionIndex;
  void contentIndex;

  if (typeof Word === "undefined") {
    return { ok: false, message: "Word is not available in this environment." };
  }
  if (officeMutationInFlight) {
    return { ok: false, message: userFacingWordBusy() };
  }
  if (hintsFromLocationOptions(options).length === 0) {
    return { ok: false, message: userFacingHintRequired() };
  }
  officeMutationInFlight = true;
  try {
    await Word.run(async (context) => {
      context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();
      const { paragraph } = await resolveParagraphFromOptions(context, options);

      const rejectResult = await rejectParagraphTrackedChanges(
        context,
        paragraph,
      );
      console.info(
        `[restoreClause] rejectTrackedChanges ok=${rejectResult.ok} hadChanges=${rejectResult.hadChanges}`,
      );

      await applyTextToParagraphByPlan(
        context,
        paragraph,
        baselineText,
        "restoreClause",
        {
          skipHighlight: true,
          // Reject API failed with leftover markup risk → force full baseline write.
          forceFull: !rejectResult.ok,
        },
      );
    });
    return { ok: true };
  } catch (e) {
    console.warn("restoreParagraphToOriginal failed", e);
    return { ok: false, message: userFacingNavigateError() };
  } finally {
    officeMutationInFlight = false;
  }
};

/**
 * Inserts a Word comment anchored to the target paragraph (best-effort).
 */
export const insertCommentAtSuggestionIndexes = async (
  sectionIndex: number | null,
  contentIndex: number | null,
  commentText: string,
  options?: SuggestionLocationOptions
): Promise<NavigateIndexesResult> => {
  void sectionIndex;
  void contentIndex;

  if (typeof Word === "undefined") {
    return { ok: false, message: "Word is not available in this environment." };
  }
  const trimmed = commentText.trim();
  if (trimmed === "") {
    return { ok: false, message: "Enter a comment before adding it to Word." };
  }
  if (hintsFromLocationOptions(options).length === 0) {
    return { ok: false, message: userFacingHintRequired() };
  }
  try {
    await Word.run(async (context) => {
      const { paragraph } = await resolveParagraphFromOptions(context, options);
      const range = paragraph.getRange();
      const rangeWithComment = range as Word.Range & {
        insertComment?: (commentText: string) => Word.Comment;
      };
      if (typeof rangeWithComment.insertComment !== "function") {
        throw new Error("COMMENTS_UNSUPPORTED");
      }
      rangeWithComment.insertComment(trimmed);
      await context.sync();
    });
    return { ok: true };
  } catch (e) {
    console.warn("insertCommentAtSuggestionIndexes failed", e);
    return { ok: false, message: userFacingNavigateError() };
  }
};
